import { useEffect, useRef } from 'react';
import { onScroll } from '../lib/scrollDriver';

/**
 * Drift an element as it crosses the viewport.
 *
 * Progress is measured from the element's centre relative to the viewport
 * centre and normalised to roughly -1 → 1, so the effect is symmetric and the
 * element sits at its natural position when centred. The transform is written
 * straight to the node rather than through React state, so scrolling never
 * triggers a re-render.
 *
 * Honours `prefers-reduced-motion`: the subscription is simply never made, and
 * any transform already applied is cleared.
 *
 * `scale` is a constant overscale, not an animated one. Drifting an image
 * inside an `overflow-hidden` frame would otherwise expose a gap at the frame
 * edge, so the layer is grown by `scale` and the travel is then clamped to the
 * headroom that growth creates — the frame always stays covered.
 *
 * @param {object} options
 * @param {number} options.speed  px of travel at the viewport edges (can be negative)
 * @param {number} options.scale  constant overscale that creates travel headroom, e.g. 0.06
 * @param {'y'|'x'} options.axis
 */
export function useParallax({ speed = 40, scale = 0, axis = 'y' } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return undefined;

    const reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      el.style.transform = '';
      return undefined;
    }

    const update = () => {
      const rect = el.getBoundingClientRect();
      const viewport = window.innerHeight || 0;

      // Skip work for anything comfortably off screen.
      if (rect.bottom < -viewport || rect.top > viewport * 2) return;

      const centre = rect.top + rect.height / 2;
      const progress = (centre - viewport / 2) / (viewport / 2 + rect.height / 2);
      const clamped = Math.max(-1, Math.min(1, progress));

      // With an overscale, never travel further than the headroom it created,
      // or the frame's edge would show through.
      const extent = axis === 'x' ? rect.width : rect.height;
      const headroom = (extent * scale) / 2;
      const limit = scale > 0 ? Math.min(Math.abs(speed), headroom) : Math.abs(speed);
      const shift = clamped * limit * (speed < 0 ? -1 : 1);

      el.style.transform =
        (axis === 'x' ? `translate3d(${shift}px, 0, 0)` : `translate3d(0, ${shift}px, 0)`) +
        (scale ? ` scale(${(1 + scale).toFixed(4)})` : '');
    };

    el.style.willChange = 'transform';
    const unsubscribe = onScroll(update);

    return () => {
      unsubscribe();
      el.style.willChange = '';
      el.style.transform = '';
    };
  }, [speed, scale, axis]);

  return ref;
}

export default useParallax;
